import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Globe, Award, Users, BookOpen, Shield, CheckCircle, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { usePageMeta } from '@/hooks/useSEO';
import SmartProfileEvalCTA from './components/SmartProfileEvalCTA';
import TrustSignalStrip from '@/components/common/TrustSignalStrip';
import PrivacyConsentNotice from '@/components/common/PrivacyConsentNotice';
import { LeadPhoneField } from '@/components/lead-quality/LeadPhoneField';
import { useLeadQualityEngine } from '@/components/lead-quality/useLeadQualityEngine';
import { SecureLeadForm } from '@/components/secure-lead/SecureLeadForm';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const countries = [
  { name: 'Germany', slug: 'germany', flag: 'DE', tagline: 'Free Tuition at Public Universities', color: '#003087' },
  { name: 'United Kingdom', slug: 'uk', flag: 'GB', tagline: 'World-Class Education & PSW Visa', color: '#001F5C' },
  { name: 'Canada', slug: 'canada', flag: 'CA', tagline: 'PR Pathways After Graduation', color: '#0052CC' },
  { name: 'Australia', slug: 'australia', flag: 'AU', tagline: 'High Employability & Quality of Life', color: '#0891B2' },
  { name: 'Ireland', slug: 'ireland', flag: 'IE', tagline: 'Europe\'s Tech Hub & Stay Back Options', color: '#16A34A' },
];

const stats = [
  { value: '10,000+', label: 'Students Placed' },
  { value: '500+', label: 'Partner Universities' },
  { value: '98%', label: 'Visa Success Rate' },
  { value: '5', label: 'Countries Covered' },
];

const services = [
  { icon: GraduationCap, title: 'University Shortlisting', desc: 'Personalized recommendations based on your profile, budget, and career goals.' },
  { icon: BookOpen, title: 'SOP & Document Prep', desc: 'Expert crafted Statements of Purpose and complete application support.' },
  { icon: Shield, title: 'Visa Assistance', desc: 'End-to-end student visa processing with near-perfect approval rates.' },
  { icon: Globe, title: 'Pre-Departure Support', desc: 'Accommodation, travel, forex, and cultural orientation before you fly.' },
  { icon: Award, title: 'Scholarship Guidance', desc: 'Access exclusive scholarships and education loan assistance.' },
  { icon: Users, title: 'Career Counselling', desc: 'Post-study career planning including internship and job placement support.' },
];

const LeadCaptureForm = () => {
  const { flags } = useFeatureFlags();
  const lqe = useLeadQualityEngine();
  const [form, setForm] = useState({ name: '', email: '', destination: '', intake: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (flags.secure_lead_pipeline) {
    return <SecureLeadForm source="study-abroad-homepage" />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const guard = await lqe.guardSubmit();
    if (!guard.ok) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: lqe.phone,
          ...guard.payloadMeta,
          service_type: 'Study Abroad',
          country: form.destination || 'Not specified',
          source: 'study-abroad-homepage',
          message: `[Study Abroad Lead] Destination: ${form.destination || 'Not specified'}, Intake: ${form.intake || 'Not specified'}. Source: study-abroad-homepage`,
        }),
      });
      if (lqe.interpretResponse(res)) return;
      lqe.markSubmitSuccess();
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold font-syne text-[#003087]">Thank You!</h3>
        <p className="text-slate-600 text-sm mt-2">Our counsellor will contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="sa-lead-form">
      <input
        type="text"
        placeholder="Full Name *"
        required
        className="sa-input"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        data-testid="sa-lead-name"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
        <input
          type="email"
          placeholder="Email Address *"
          required
          className="sa-input"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="sa-lead-email"
        />
        <LeadPhoneField engine={lqe} testId="sa-lead-phone" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <select
          className="sa-input"
          value={form.destination}
          onChange={(e) => setForm({ ...form, destination: e.target.value })}
          data-testid="sa-lead-destination"
        >
          <option value="">Preferred Destination</option>
          <option value="Germany">Germany</option>
          <option value="UK">United Kingdom</option>
          <option value="Canada">Canada</option>
          <option value="Australia">Australia</option>
          <option value="Ireland">Ireland</option>
        </select>
        <select
          className="sa-input"
          value={form.intake}
          onChange={(e) => setForm({ ...form, intake: e.target.value })}
          data-testid="sa-lead-intake"
        >
          <option value="">Preferred Intake</option>
          <option value="Fall 2026">Fall 2026</option>
          <option value="Spring 2027">Spring 2027</option>
          <option value="Fall 2027">Fall 2027</option>
        </select>
      </div>
      <PrivacyConsentNotice tone="light" testid="sa-lead-consent" />
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3.5 rounded-full transition-all sa-pulse-cta disabled:opacity-60"
        data-testid="sa-lead-submit"
      >
        {submitting ? 'Submitting...' : 'Get Free Counselling'}
      </button>
      <p className="text-xs text-slate-400 text-center">No spam. Your data is safe with us.</p>
    </form>
  );
};

const StudyAbroadHomePage = () => {
  // SEO — main Study Abroad hub
  usePageMeta(
    'Study Abroad Consultants in India 2026: UK · Germany · Canada · Australia',
    'MEA-approved study abroad consultants for Indian students. UK, Germany, Canada, Australia & Ireland — admissions, scholarships, visa & PR pathways. 10,000+ placements.'
  );

  return (
    <div data-testid="sa-homepage">
      {/* ─── Hero Section ─── */}
      <section className="sa-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#C5A028] rounded-full blur-[120px]" />
          <div className="absolute bottom-10 right-20 w-96 h-96 bg-[#0891B2] rounded-full blur-[150px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
                <Shield className="w-3.5 h-3.5 text-[#C5A028]" />
                <span className="text-xs text-white/90 font-medium tracking-wide">MEA Approved · 10,000+ Placements Since 2015</span>
              </div>
              <h1 className="sa-display text-4xl sm:text-5xl lg:text-[58px] font-semibold text-white leading-[0.95] mb-6">
                <span className="block">
                  Study Abroad with <span className="italic font-medium">MEA-Approved</span>
                </span>
                <span className="block text-[#C5A028] mt-2">ZAFCO Consultants</span>
                {/* Secondary SEO keyword tier — inside same H1, visually de-emphasised */}
                <span className="block text-base md:text-lg lg:text-xl font-medium font-manrope text-slate-300 mt-5 tracking-normal leading-snug">
                  <span className="text-[#C5A028]">Study in UK</span>
                  <span className="mx-2 text-slate-500" aria-hidden="true">|</span>
                  <span className="text-white">Germany</span>
                  <span className="mx-2 text-slate-500" aria-hidden="true">|</span>
                  <span className="text-white">Canada</span>
                  <span className="mx-2 text-slate-500" aria-hidden="true">|</span>
                  <span className="text-white">Australia</span>
                  <span className="mx-2 text-slate-500" aria-hidden="true">|</span>
                  <span className="text-white">Ireland</span>
                </span>
              </h1>
              <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
                International education designed for global career success. Personalised university
                shortlists, scholarship guidance, and a direct bridge to ZAFCO's worldwide employer
                network — across <strong className="text-white/90">Germany, UK, Canada, Australia, Ireland &amp; Dubai</strong>.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/study-abroad/visa-eligibility/?source=home-hero-cta"
                  className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
                  data-testid="sa-hero-cta"
                >
                  Free Eligibility Evaluation <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/study-abroad/book-counselling/?source=home-hero-secondary"
                  className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-[#C5A028] text-white hover:text-[#C5A028] font-semibold text-sm px-8 py-3.5 rounded-full transition-all"
                >
                  Talk to a Counsellor
                </Link>
              </div>
            </div>

            {/* Right - Lead Form */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-2xl" data-testid="sa-hero-form-card">
              <h2 className="text-lg font-bold font-syne text-[#003087] mb-1">
                Get Expert Guidance
              </h2>
              <p className="text-slate-500 text-sm mb-5">Fill in your details for a free consultation</p>
              <LeadCaptureForm />
            </div>
          </div>
        </div>
      </section>

      {/* E-E-A-T trust signals — MEA license, founded year, placements, success */}
      <TrustSignalStrip />

      {/* ─── Stats Section ─── */}
      <section className="bg-[#001F5C] py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="sa-stat-card rounded-xl p-5 text-center">
                <div className="text-3xl lg:text-4xl font-bold font-syne text-[#C5A028]">{s.value}</div>
                <div className="text-sm text-slate-300 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Study Destinations ─── */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">
              Top Study Destinations
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Explore the best countries for higher education with post-study work opportunities and PR pathways.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {countries.map((c) => (
              <Link
                key={c.slug}
                to={`/study-abroad/${c.slug}/`}
                className="sa-country-card group block bg-white rounded-2xl overflow-hidden border border-slate-100"
                data-testid={`sa-country-${c.slug}`}
              >
                <div className="h-3 w-full" style={{ background: c.color }} />
                <div className="p-5">
                  <div className="text-3xl mb-3">{getFlagEmoji(c.flag)}</div>
                  <h3 className="text-base font-bold font-syne text-slate-900 group-hover:text-[#003087] transition-colors">
                    {c.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{c.tagline}</p>
                  <div className="mt-4 flex items-center gap-1 text-[#003087] text-xs font-semibold group-hover:gap-2 transition-all">
                    Explore <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Services ─── */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">
              Our Services
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              End-to-end support from university selection to post-landing assistance.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc) => (
              <div
                key={svc.title}
                className="sa-feature-card bg-white rounded-xl p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-[#003087]/10 flex items-center justify-center mb-4">
                  <svc.icon className="w-6 h-6 text-[#003087]" />
                </div>
                <h3 className="text-base font-bold font-syne text-slate-900 mb-2">{svc.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why ZAFCO ─── */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="sa-gold-line mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-5">
                Why Choose ZAFCO Study Abroad?
              </h2>
              <div className="space-y-4">
                {[
                  'Government of India (MEA) Approved Agency',
                  '15+ Years of Global Mobility Experience',
                  '500+ Partner Universities Worldwide',
                  '98% Student Visa Success Rate',
                  'Free Tuition Options in Germany',
                  'End-to-End Support from Application to Landing',
                  'Dedicated Post-Study Career Guidance',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#C5A028] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{point}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/study-abroad/about-zafco/"
                className="inline-flex items-center gap-2 mt-8 text-[#003087] font-semibold text-sm hover:gap-3 transition-all"
              >
                Learn more about us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-gradient-to-br from-[#003087] to-[#001F5C] rounded-2xl p-8 lg:p-10 text-white">
              <h3 className="text-xl font-bold font-syne mb-4">Start Your Journey Today</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Join thousands of Indian students who have successfully secured admissions in
                top global universities through ZAFCO.
              </p>
              <Link
                to="/study-abroad/book-counselling/"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all"
              >
                Book Free Counselling <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ─── */}
      <section className="sa-hero-gradient py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-syne text-white mb-4">
            Ready to Study Abroad?
          </h2>
          <p className="text-base text-slate-300 mb-8 max-w-2xl mx-auto">
            Take the first step towards your international education. Our expert counsellors
            are ready to help you find the perfect university and program.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/study-abroad/book-counselling/"
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
            >
              Book Free Counselling <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/study-abroad/free-profile-evaluation/"
              className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-all"
            >
              Free Profile Evaluation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default StudyAbroadHomePage;
